import React, { useState } from 'react';
import { Users, UserPlus, Trash2, Mail, Shield, Edit } from 'lucide-react';
import { $api } from '../../api';
import { useOrganization } from '../../contexts/OrganizationContext';
import ErrorMessage from '../ErrorMessage';
import SuccessMessage from '../SuccessMessage';
import { useAuth } from '../../App';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  organizationId: string;
}

const InviteUserModal: React.FC<InviteModalProps> = ({ isOpen, onClose, onSuccess, organizationId }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');

  const inviteUserMutation = $api.useMutation('post', '/api/organizations/{organization_id}/users');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    inviteUserMutation.mutate(
      {
        params: { path: { organization_id: organizationId } },
        body: { email, role },
      },
      {
        onSuccess: () => {
          onSuccess();
          setEmail('');
          setRole('member');
          onClose();
        },
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <UserPlus className="h-5 w-5 mr-2" />
          Inviter un utilisateur
        </h3>

        {inviteUserMutation.error && (
          <ErrorMessage error={inviteUserMutation.error} className="mb-4" />
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="utilisateur@example.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rôle *
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'member')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="member">Membre</option>
              <option value="admin">Administrateur</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Les administrateurs peuvent gérer les utilisateurs et les paramètres
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => {
                onClose();
                setEmail('');
                setRole('member');
                inviteUserMutation.reset();
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={inviteUserMutation.isPending}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={inviteUserMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {inviteUserMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Envoi...</span>
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4" />
                  <span>Envoyer l'invitation</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface RemoveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  organizationId: string;
  userId: string;
  userName: string;
}

const RemoveUserModal: React.FC<RemoveModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  organizationId, 
  userId,
  userName 
}) => {
  const removeUserMutation = $api.useMutation('delete', '/api/organizations/{organization_id}/users/{user_id}');

  const handleRemove = () => {
    removeUserMutation.mutate(
      {
        params: { 
          path: { 
            organization_id: organizationId,
            user_id: userId 
          } 
        },
      },
      {
        onSuccess: () => {
          onSuccess();
          onClose();
        },
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center text-red-600">
          <Trash2 className="h-5 w-5 mr-2" />
          Retirer l'utilisateur
        </h3>

        {removeUserMutation.error && (
          <ErrorMessage error={removeUserMutation.error} className="mb-4" />
        )}

        <p className="text-gray-600 mb-6">
          Êtes-vous sûr de vouloir retirer <strong>{userName}</strong> de cette organisation ? 
          Cette action est irréversible.
        </p>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => {
              onClose();
              removeUserMutation.reset();
            }}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            disabled={removeUserMutation.isPending}
          >
            Annuler
          </button>
          <button
            onClick={handleRemove}
            disabled={removeUserMutation.isPending}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {removeUserMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Suppression...</span>
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                <span>Retirer</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

interface UpdateRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  organizationId: string;
  userId: string;
  userName: string;
  currentRole: string;
}

const UpdateRoleModal: React.FC<UpdateRoleModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  organizationId, 
  userId,
  userName,
  currentRole
}) => {
  const [newRole, setNewRole] = useState<'owner' | 'admin' | 'member'>(currentRole as 'owner' | 'admin' | 'member');
  const updateRoleMutation = $api.useMutation('put', '/api/organizations/{organization_id}/users/{user_id}');

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newRole === currentRole) {
      onClose();
      return;
    }

    updateRoleMutation.mutate(
      {
        params: { 
          path: { 
            organization_id: organizationId,
            user_id: userId 
          } 
        },
        body: { role: newRole },
      },
      {
        onSuccess: () => {
          onSuccess();
          onClose();
        },
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Edit className="h-5 w-5 mr-2 text-blue-600" />
          Modifier le rôle
        </h3>

        {updateRoleMutation.error && (
          <ErrorMessage error={updateRoleMutation.error} className="mb-4" />
        )}

        <p className="text-gray-600 mb-4">
          Modifier le rôle de <strong>{userName}</strong> dans cette organisation.
        </p>

        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nouveau rôle *
            </label>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as 'owner' | 'admin' | 'member')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              autoFocus
            >
              <option value="member">Membre</option>
              <option value="admin">Administrateur</option>
              <option value="owner">Propriétaire</option>
            </select>
            <div className="mt-2 text-xs text-gray-500 space-y-1">
              <p><strong>Membre :</strong> Accès aux fonctionnalités de base</p>
              <p><strong>Administrateur :</strong> Peut gérer les utilisateurs et les paramètres</p>
              <p><strong>Propriétaire :</strong> Contrôle total de l'organisation</p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => {
                onClose();
                setNewRole(currentRole as 'owner' | 'admin' | 'member');
                updateRoleMutation.reset();
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={updateRoleMutation.isPending}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={updateRoleMutation.isPending || newRole === currentRole}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateRoleMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Mise à jour...</span>
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4" />
                  <span>Mettre à jour</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const OrganizationUsers: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { currentOrganization } = useOrganization();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [removeModalState, setRemoveModalState] = useState<{
    isOpen: boolean;
    userId: string;
    userName: string;
  }>({
    isOpen: false,
    userId: '',
    userName: '',
  });
  const [updateRoleModalState, setUpdateRoleModalState] = useState<{
    isOpen: boolean;
    userId: string;
    userName: string;
    currentRole: string;
  }>({
    isOpen: false,
    userId: '',
    userName: '',
    currentRole: '',
  });
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch users for current organization
  const usersQuery = $api.useQuery(
    'get', 
    '/api/organizations/{organization_id}/users',
    {
      params: {
        path: {
          organization_id: currentOrganization?.id || '',
        },
      },
    },
    {
      enabled: !!currentOrganization?.id,
    }
  );

  const handleInviteSuccess = () => {
    setSuccess('Invitation envoyée avec succès');
    usersQuery.refetch();
    setTimeout(() => setSuccess(null), 5000);
  };

  const handleRemoveSuccess = () => {
    setSuccess('Utilisateur retiré avec succès');
    usersQuery.refetch();
    setTimeout(() => setSuccess(null), 5000);
  };

  const handleUpdateRoleSuccess = () => {
    setSuccess('Rôle mis à jour avec succès');
    usersQuery.refetch();
    setTimeout(() => setSuccess(null), 5000);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'admin':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'member':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner':
        return 'Propriétaire';
      case 'admin':
        return 'Administrateur';
      case 'member':
        return 'Membre';
      default:
        return role;
    }
  };

  if (!currentOrganization) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center text-gray-600">
          Aucune organisation sélectionnée
        </div>
      </div>
    );
  }

  if (usersQuery.isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Chargement...</span>
        </div>
      </div>
    );
  }

  const users = usersQuery.data?.users || [];
  const canManageUsers = currentOrganization.role === 'owner' || currentOrganization.role === 'admin';

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header with organization name */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Users className="h-5 w-5 mr-2" />
                Utilisateurs de l'organisation
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Organisation: <span className="font-medium text-gray-900">{currentOrganization.name}</span>
              </p>
            </div>
            {canManageUsers && (
              <button
                onClick={() => setIsInviteModalOpen(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <UserPlus className="h-4 w-4" />
                <span>Inviter un utilisateur</span>
              </button>
            )}
          </div>
        </div>

        {/* Success/Error Messages */}
        <div className="px-6 pt-4">
          {usersQuery.error && (
            <ErrorMessage error={usersQuery.error} className="mb-4" />
          )}

          {success && (
            <SuccessMessage message={success} className="mb-4" />
          )}
        </div>

        {/* Users List */}
        <div className="p-6">
          {users.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>Aucun utilisateur trouvé</p>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-blue-600 font-semibold text-sm">
                            {(user.first_name?.charAt(0) || '') + (user.last_name?.charAt(0) || '') || user.email.substring(0, 2)}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-sm text-gray-500 truncate">{user.email}</p>
                        <p className="text-xs text-gray-400">{user.company_name}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getRoleBadgeColor(user.role)}`}>
                      <Shield className="h-3 w-3 inline mr-1" />
                      {getRoleLabel(user.role)}
                    </span>

                    {canManageUsers && user.email !== currentUser?.email && (
                      <>
                        <button
                          onClick={() => {
                            setUpdateRoleModalState({
                              isOpen: true,
                              userId: user.id,
                              userName: `${user.first_name} ${user.last_name}`,
                              currentRole: user.role,
                            });
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Modifier le rôle"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setRemoveModalState({
                              isOpen: true,
                              userId: user.id,
                              userName: `${user.first_name} ${user.last_name}`,
                            });
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Retirer l'utilisateur"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Info section */}
          {!canManageUsers && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600">
                Vous n'avez pas les permissions nécessaires pour gérer les utilisateurs de cette organisation.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Permissions Info */}
      <div className="pt-6">
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
            <Shield className="h-4 w-4 mr-2" />
            Permissions par rôle
          </h4>
          <div className="text-sm text-blue-800 space-y-1.5">
            <p>
              <strong className="text-purple-700">Propriétaires :</strong> Contrôle total de l'organisation
            </p>
            <p>
              <strong className="text-blue-700">Administrateurs :</strong> Mêmes permissions que les propriétaires, sauf qu'ils ne peuvent pas retirer les propriétaires de l'organisation
            </p>
            <p>
              <strong className="text-gray-700">Membres :</strong> Peuvent accéder à toutes les fonctionnalités, sauf la gestion des utilisateurs et des factures
            </p>
          </div>
        </div>
      </div>

      {/* Modals */}
      {currentOrganization && (
        <>
          <InviteUserModal
            isOpen={isInviteModalOpen}
            onClose={() => setIsInviteModalOpen(false)}
            onSuccess={handleInviteSuccess}
            organizationId={currentOrganization.id}
          />

          <UpdateRoleModal
            isOpen={updateRoleModalState.isOpen}
            onClose={() => setUpdateRoleModalState({ isOpen: false, userId: '', userName: '', currentRole: '' })}
            onSuccess={handleUpdateRoleSuccess}
            organizationId={currentOrganization.id}
            userId={updateRoleModalState.userId}
            userName={updateRoleModalState.userName}
            currentRole={updateRoleModalState.currentRole}
          />

          <RemoveUserModal
            isOpen={removeModalState.isOpen}
            onClose={() => setRemoveModalState({ isOpen: false, userId: '', userName: '' })}
            onSuccess={handleRemoveSuccess}
            organizationId={currentOrganization.id}
            userId={removeModalState.userId}
            userName={removeModalState.userName}
          />
        </>
      )}
    </>
  );
};

export default OrganizationUsers;

